
import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translate } from './languages/languageUtils';
import { stripLanguageFromPath, getLocalizedPathForLanguage } from './languages/languageUtils';
import { getBestLanguageMatch, storeLanguagePreference } from '@/utils/browserLanguageDetector';
import { extractLanguageFromPath } from '@/utils/routing/multilingualRouting';
import { HardcodedContentDetector } from '@/utils/validation/HardcodedContentDetector';
import type { Language } from './languages/languageData';
import { languageNames, languageFlags } from './languages/languageData';

// Re-export language data
export type { Language };
export { languageNames, languageFlags } from './languages/languageData';

interface LanguageContextType {
  language: Language;
  t: (key: string) => string;
  changeLanguage: (lang: Language) => void;
  getLocalizedPath: (path: string) => string;
  getLocalizedPathForLanguage: (path: string, lang: Language) => string;
  changeLanguageAndNavigate: (lang: Language) => void;
  languageNames: typeof languageNames;
  languageFlags: typeof languageFlags;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Inner component that uses router hooks
const LanguageProviderInner: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Safely access router context
  let location, navigate;
  try {
    location = useLocation();
    navigate = useNavigate();
  } catch (error) {
    console.warn('Router context not available, using fallback');
    location = { pathname: '/' };
    navigate = () => {};
  }
  
  const [language, setLanguage] = useState<Language>('en');

  // Initialize language based on URL path with browser detection fallback
  useEffect(() => {
    const path = location.pathname;
    console.log('Current path:', path);
    
    // For root path, always use English
    if (path === '/') {
      console.log('Root path detected, using English');
      setLanguage('en');
      storeLanguagePreference('en');
      return;
    }
    
    // For other paths, extract language from URL
    const urlLanguage = extractLanguageFromPath(path);
    if (urlLanguage) {
      // Only use supported main languages for the context
      const supportedLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
      if (supportedLanguages.includes(urlLanguage as Language)) {
        console.log('URL language detected:', urlLanguage);
        setLanguage(urlLanguage as Language);
        storeLanguagePreference(urlLanguage as Language);
      } else {
        // Fallback to English for unsupported languages
        console.log('Unsupported language in URL, using English');
        setLanguage('en');
        storeLanguagePreference('en');
      }
    } else {
      // Fallback to English if no language detected in URL
      console.log('No language in URL, using English');
      setLanguage('en');
      storeLanguagePreference('en');
    }
  }, [location.pathname]);

  // Translation function
  const t = (key: string): string => {
    // First try the robust translation system
    try {
      const robustTranslations = HardcodedContentDetector.getLanguageReplacements(language);
      if (robustTranslations[key]) {
        return robustTranslations[key];
      }
    } catch (error) {
      console.warn('Robust translation failed for key:', key);
    }

    // Fallback to original translation system
    const translation = translate(key, language);
    if (translation === key) {
      console.warn(`Translation missing for key "${key}" in language "${language}"`);
    }
    return translation;
  };

  // Change language function with enhanced storage
  const changeLanguage = (lang: Language): void => {
    console.log('Changing language to:', lang);
    setLanguage(lang);
    storeLanguagePreference(lang);
  };

  // Get localized path for current language
  const getLocalizedPath = (path: string): string => {
    return getLocalizedPathForLanguage(path, language);
  };

  // Change language and navigate to equivalent page in new language
  const changeLanguageAndNavigate = (lang: Language): void => {
    if (lang === language) return;
    
    console.log(`Changing language from ${language} to ${lang}`);
    const currentPath = location.pathname;
    
    // Obtener la ruta base sin idioma
    const cleanPath = stripLanguageFromPath(currentPath);
    console.log('Clean path:', cleanPath);
    
    // Generar nueva ruta localizada
    const newPath = getLocalizedPathForLanguage(cleanPath, lang);
    console.log('New localized path:', newPath);
    
    changeLanguage(lang);
    navigate(newPath, { replace: true });
  };

  return (
    <LanguageContext.Provider
      value={{
        language,
        t,
        changeLanguage,
        getLocalizedPath,
        getLocalizedPathForLanguage,
        changeLanguageAndNavigate,
        languageNames,
        languageFlags,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// Main provider component that doesn't use router hooks directly
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  return <LanguageProviderInner>{children}</LanguageProviderInner>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
